import * as CL from '@design-system/component-library';
import { useLoaderData, useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../../../../common/constants/dsClasses.js';
import { paths } from '../../../../../common/constants/pathVariables.js';
import { DeviceListStatusCoCo } from '../../../../../common/enums.js';
import { createEmployeeProductList, patchEmployeeProductList } from '../../../../../common/fetch.js';
import {
  deviceListSavedAsDraftMsg,
  deviceListsMsg,
  newDeviceListMsg,
  omaElisaForCompaniesMsg,
  savingTheDataFailedMsg,
  summaryMsg,
  t,
} from '../../../../../common/i18n/index.js';
import { isMultiBiz } from '../../../../../common/utils/accountUtils.js';
import {
  DEVICE_LIST_SUMMARY_PARAM,
  toDeviceListAction,
  toDeviceListFormValues,
  toEmployeeProductListRequest,
  toPatchEmployeeProductListRequest,
} from '../../../../../common/utils/deviceListFormUtilsCoCo.js';
import { getDeviceListPath, getDeviceListsTabPath } from '../../../../../common/utils/deviceListTabsUtilsCoCo.js';
import { BreadCrumbs } from '../../../../../components/BreadCrumbs/index.js';
import { DetailsWrapper } from '../../../../../components/DetailsWrapper/index.js';
import { DeviceListFormCoCo } from '../../../../../components/DeviceListFormCoCo/DeviceListFormCoCo.js';
import { DeviceListSummaryCoCo } from '../../../../../components/DeviceListFormCoCo/DeviceListSummaryCoCo.js';
import { useAuth } from '../../../AuthProvider.js';
import { addNotification } from '../../../notificationStore.js';
import type { DeviceListEditLoaderDataCoCo, DeviceListNewLoaderDataCoCo } from '../../../../../common/loaders.js';
import type { DeviceListFormValuesCoCo } from '../../../../../common/utils/deviceListFormUtilsCoCo.js';
import type { BreadCrumbName, BreadCrumbNameAndPath } from '../../../../../components/BreadCrumbs/index.js';

const getSearch = (search: string, isSummary: boolean) => {
  const params = new URLSearchParams(search);
  if (isSummary) {
    params.set(DEVICE_LIST_SUMMARY_PARAM, '1');
  } else {
    params.delete(DEVICE_LIST_SUMMARY_PARAM);
  }
  return params.size > 0 ? `?${params}` : '';
};

interface DeviceListFormPageCoCoProps {
  data: DeviceListNewLoaderDataCoCo;
  id: string;
  heading: string;
  // The crumbs after the device lists
  breadCrumbPaths: [...BreadCrumbNameAndPath[], BreadCrumbName];
  initialValues?: DeviceListFormValuesCoCo;
  cancelHref: string;
  isEdit?: boolean;
  // Saves the list and returns the path to navigate to after saving
  save: (values: DeviceListFormValuesCoCo, status: DeviceListStatusCoCo) => Promise<string>;
}

const DeviceListFormPageCoCo = ({
  data,
  id,
  heading,
  breadCrumbPaths,
  initialValues,
  cancelHref,
  isEdit,
  save,
}: DeviceListFormPageCoCoProps) => {
  const { mdmId, companyInfo } = data;
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const { authenticatedUser } = useAuth();
  // The company can only be selected for a new list, so it's shown in the header when editing
  const showCompanyName = isEdit || !isMultiBiz(authenticatedUser);
  // The form values are kept in the history state, so that they are restored when returning from the summary
  const values = state as DeviceListFormValuesCoCo | null;
  const isSummary = Boolean(values) && new URLSearchParams(search).get(DEVICE_LIST_SUMMARY_PARAM) === '1';

  const onSave = async (formValues: DeviceListFormValuesCoCo, status: DeviceListStatusCoCo) => {
    const isPublished = status === DeviceListStatusCoCo.PUBLISHED;
    try {
      const nextPath = await save(formValues, status);
      addNotification(isPublished ? t.BTQA('Catalogue published') : t.P7JV(deviceListSavedAsDraftMsg));
      navigate(nextPath);
    } catch {
      addNotification(
        isPublished ? t.YY7Z('Failed to publish the catalogue') : t.RQA1(savingTheDataFailedMsg),
        'error'
      );
    }
  };

  return (
    <DetailsWrapper
      detailsTop={
        <BreadCrumbs
          breadCrumbPaths={[
            { name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME },
            { name: t.COBB(deviceListsMsg), path: getDeviceListsTabPath(mdmId) },
            ...breadCrumbPaths,
          ]}
        />
      }
      id={isSummary ? 'device-list-summary' : id}
      heading={heading}
      headingTop={isSummary ? t.VDB4(summaryMsg) : undefined}
      headingBottom={showCompanyName ? companyInfo?.companyName : undefined}
      heroPicto="catalog"
    >
      {isSummary && values ? (
        <DeviceListSummaryCoCo
          data={data}
          values={values}
          onReturn={() => navigate(pathname + getSearch(search, false), { state: values })}
          onSave={status => onSave(values, status)}
        />
      ) : (
        <>
          <CL.Disclaimer
            className={dsClass.MARGIN_BOTTOM_4}
            disclaimerType="info"
            icon={<CL.Icon icon="information" type="regular" aria-hidden="true" />}
            ariaRole="status"
            ariaLive="polite"
            visible
            text={t.JHXQ(
              'Define the device list settings for employee orders and select the device selection to be used for the list.'
            )}
          />
          {/* The form is reset when the company changes, as the billing accounts and selections are company specific */}
          <DeviceListFormCoCo
            key={mdmId}
            data={data}
            initialValues={values ?? initialValues}
            onSubmit={formValues => navigate(pathname + getSearch(search, true), { state: formValues })}
            cancelHref={cancelHref}
            isCompanySelectable={!isEdit}
          />
        </>
      )}
    </DetailsWrapper>
  );
};

export const CompanyInfoDeviceListNewPageCoCo = () => {
  const data = useLoaderData() as DeviceListNewLoaderDataCoCo;
  const { mdmId } = data;

  return (
    <DeviceListFormPageCoCo
      data={data}
      id="device-list-new"
      heading={t.MPFC(newDeviceListMsg)}
      breadCrumbPaths={[{ name: t.MPFC(newDeviceListMsg) }]}
      cancelHref={getDeviceListsTabPath(mdmId)}
      save={async (values, status) => {
        await createEmployeeProductList(
          toEmployeeProductListRequest(values, mdmId, status),
          toDeviceListAction(status)
        );
        return getDeviceListsTabPath(mdmId);
      }}
    />
  );
};

export const CompanyInfoDeviceListEditPageCoCo = () => {
  const data = useLoaderData() as DeviceListEditLoaderDataCoCo;
  const { mdmId, deviceList, deviceSelections } = data;
  const uuid = deviceList.uuid ?? '';
  const detailsPath = getDeviceListPath(uuid, mdmId);

  return (
    <DeviceListFormPageCoCo
      data={data}
      id={`device-list-edit-${uuid}`}
      heading={t.NC59('Edit device list')}
      breadCrumbPaths={[{ name: deviceList.name ?? '', path: detailsPath }, { name: t.NC59('Edit device list') }]}
      initialValues={toDeviceListFormValues(deviceList, deviceSelections)}
      cancelHref={detailsPath}
      isEdit
      save={async (values, status) => {
        // The patch creates a replacement version of the list with a new uuid
        const updated = await patchEmployeeProductList(
          uuid,
          toPatchEmployeeProductListRequest(values, status),
          toDeviceListAction(status),
          mdmId
        );
        return updated.uuid ? getDeviceListPath(updated.uuid, mdmId) : getDeviceListsTabPath(mdmId);
      }}
    />
  );
};
